import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import { browserHistory } from 'react-router';
import moment from 'moment';

import Button from '../../Button';
import ErrorBlock from '../../common/ErrorBlock';
import { useVisitInfoForm } from '../utils/useVisitInfoFormState';
import { VisitInfoForm } from './VisitInfoForm';
import Loader from '../../common/Loader';
import TimePickerButton from '../../common/TimePickerButton';
import {
  getVisitFormOptions,
  getSelectableList,
} from '../utils/getVisitFormData';
import { CIRCLE_PROVIDER } from '../../../constants/strings';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import { isBetweenTimeRange } from '../../../utils/converters/dateConverters';
import { getSelectOptions } from '../../ProfileLayout/utils/getSelectOptions';
import {
  PAYMENT_STATUS,
  DASHBOARD_TIME_VALIDATION_RULES as TIME_RULES,
} from '../../../constants/props';
import { DASHBOARD_RECORD_CAPACITY_ERRORS } from '../../../constants/errors';
import { DASHBOARD_RECORD, PATIENT_INFO } from '../../../constants/routes';
import addRecordSaga from '../../../redux/dashboard/sagas/add-record-saga';
import { useToggleFlag } from '../../../hocs/useToogleFlag';
import ActionConfirmation from '../../common/ActionConfirmation';
import { useInjectSaga } from '../../../redux/injectSaga';
import {
  convertVisitTime,
  getRecordVisitTime,
} from '../utils/convertVisitTime';

import { enhance } from './enhance';

const isValidDayOfWeek = list => {
  const today = moment()
    .format('dddd')
    .toLocaleLowerCase();
  return list[today];
};

const DashboardVisitInfo = ({
  errors,
  formatMessage,
  pending,
  rejected,
  locationsReq,
  locationsList,
  createAppointment,
  patient,
  getProviders,
  providersList,
  loadingLocations,
  isLoadingProviders,
  clearAppointmentReq,
}) => {
  useInjectSaga({
    key: 'addRecordSaga',
    saga: addRecordSaga,
  });

  const { handleFormChange, formState, isValid } = useVisitInfoForm();

  const [pickedTime, setPickedTime] = useState('');
  const [error, setError] = useState('');
  const location = formState[PROFILE_FIELDS_TYPES.LOCATION];
  const service = formState[PROFILE_FIELDS_TYPES.SERVICE];
  const [recordErrors, setRecordError] = useState(null);
  const [opened, openConfirm, closeConfirm] = useToggleFlag();

  useEffect(() => {
    if (rejected) {
      const code = get(errors, 'code');
      if (DASHBOARD_RECORD_CAPACITY_ERRORS.includes(code)) {
        openConfirm();
      } else {
        setRecordError(errors);
      }
    }
  }, [rejected, errors]);

  const locationSchedule = useMemo(() => {
    const currentLocation = locationsList.find(({ id }) => id === location);
    return get(currentLocation, 'schedule');
  }, [locationsList, location]);

  const { startTime, endTime, bookingTimeOffset, weekdays } =
    locationSchedule || {};

  useEffect(() => {
    if (isEmpty(patient)) {
      browserHistory.push(`${DASHBOARD_RECORD}/${PATIENT_INFO}`);
    } else {
      locationsReq({
        creatableOnDashboard: true,
      });
    }
  }, []);

  useEffect(() => {
    const isValidDay = location ? isValidDayOfWeek(weekdays) : true;

    if (!isValidDay) {
      const validationError = formatMessage({
        id: 'dashboard.record.notWorkingDay',
      });
      setError(validationError);
    } else {
      setError('');
    }
  }, [location, weekdays]);

  useEffect(() => {
    setPickedTime('');
  }, [location]);

  const serviceList = useMemo(() => {
    return getSelectableList(locationsList, location, 'services');
  }, [location, locationsList]);

  const visitReasonList = useMemo(() => {
    return getSelectableList(serviceList, service, 'reasonTypes');
  }, [service, serviceList]);

  const timePickerBtnText = useMemo(() => {
    return formatMessage({
      id: pickedTime
        ? 'dashboard.record.changeTime'
        : 'dashboard.record.chooseTime',
    });
  }, [pickedTime]);

  const inlineBtnText = useMemo(() => {
    return formatMessage({
      id: pickedTime
        ? 'dashboard.record.addInLine'
        : 'dashboard.record.addInLineNow',
    });
  }, [pickedTime]);

  const handleSubmit = e => {
    e.preventDefault();
    onCreateAppointment(false);
  };

  const onCreateAppointment = ignoreCapacityLimit => {
    const time = pickedTime
      ? getRecordVisitTime(pickedTime, TIME_RULES.format)
      : null;

    const data = {
      appointment: { ...formState, startTime: time, ignoreCapacityLimit },
      patient,
    };
    createAppointment(data);
  };

  const capacityText = useMemo(() => {
    const message = get(errors, 'meta.message');
    return message || 'errors.common';
  }, [errors]);

  const handleCloseConfirm = () => {
    clearAppointmentReq();
    closeConfirm();
  };

  const handleTimeSave = time => {
    setPickedTime(time);
  };

  const debouncedFetch = useCallback(
    debounce(search => {
      if (!search) {
        return;
      }
      getProviders({
        type: CIRCLE_PROVIDER,
        query: search,
        limit: 20,
        dashboardProviderSearch: true,
      });
    }, 100),
    [],
  );

  function handleSearchProvider(inputValue) {
    debouncedFetch(inputValue);
  }

  const validateTime = time => {
    const pickerTime = convertVisitTime(time, TIME_RULES.format);
    const recordEndTimeWithOffset = moment(endTime, TIME_RULES.format)
      .subtract(bookingTimeOffset, 'minutes')
      .utcOffset(endTime)
      .format(TIME_RULES.format);

    const workingTime = {
      startTime,
      endTime: recordEndTimeWithOffset,
      format: TIME_RULES.format,
    };

    const isValidTime = isBetweenTimeRange({
      ...workingTime,
      time: moment(pickerTime, TIME_RULES.format)
        .utcOffset(startTime)
        .format(TIME_RULES.format),
    });

    if (!isValidTime)
      return formatMessage(
        {
          id: 'dashboard.record.validTime',
        },
        {
          startTime: moment(startTime, TIME_RULES.format)
            .local()
            .format('HH:mm A'),
          endTime: moment(recordEndTimeWithOffset, TIME_RULES.format)
            .local()
            .format('HH:mm A'),
        },
      );
  };

  if (loadingLocations) return <Loader />;

  return (
    <div styleName="section">
      <div styleName={cn('content', { disabled: pending })}>
        <form onSubmit={handleSubmit}>
          <label styleName="form--label">
            {formatMessage({ id: 'dashboard.record.aboutVisit' })}
          </label>

          <VisitInfoForm
            {...formState}
            formatMessage={formatMessage}
            errors={recordErrors}
            onChange={handleFormChange}
            locationsList={getVisitFormOptions(locationsList)}
            servicesList={getVisitFormOptions(serviceList)}
            reasonList={getVisitFormOptions(visitReasonList)}
            paymentStatusList={getSelectOptions(Object.keys(PAYMENT_STATUS))}
            searchProvider={handleSearchProvider}
            providersList={getVisitFormOptions(
              providersList,
              'providerProfileId',
              'displayableName',
            )}
            isLoadingProviders={isLoadingProviders}
          />

          <If condition={error}>
            <ErrorBlock errors={error} className="mt3" />
          </If>
          <div styleName="btn-block">
            <If condition={Boolean(pickedTime)}>
              <span className="mr2 f-14 o-70">
                {formatMessage({ id: 'dashboard.record.visitTime' })}{' '}
                {moment(pickedTime, 'HH:mm').format('HH:mm A')}
              </span>
            </If>
            <TimePickerButton
              title={timePickerBtnText}
              saveButtonTitle={formatMessage({ id: 'common.ok' })}
              cancelButtonTitle={formatMessage({ id: 'common.cancel' })}
              disabled={!isValid || pending}
              onSave={handleTimeSave}
              validateTime={validateTime}
              format="formatted24"
            />
            <Button
              className="mr3"
              type="submit"
              raised
              color="primary"
              disabled={!isValid || pending}
            >
              {inlineBtnText}
            </Button>
          </div>
        </form>
        <ActionConfirmation
          width="xsmall"
          danger={false}
          opened={opened}
          body={capacityText}
          title={formatMessage({ id: 'dashboard.modal.title' })}
          actionButtonText={formatMessage({ id: 'surveys.actions.proceed' })}
          onClose={handleCloseConfirm}
          onSubmit={() => onCreateAppointment(true)}
        />
      </div>
    </div>
  );
};

DashboardVisitInfo.propTypes = {
  formatMessage: PropTypes.func,
  pending: PropTypes.bool,
  rejected: PropTypes.bool,
  errors: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  locationsReq: PropTypes.func,
  createAppointment: PropTypes.func,
  locationsList: PropTypes.array,
  patient: PropTypes.object,
  getProviders: PropTypes.func,
  providersList: PropTypes.array,
  loadingLocations: PropTypes.bool,
  isLoadingProviders: PropTypes.bool,
  clearAppointmentReq: PropTypes.func,
};

export default enhance(DashboardVisitInfo);



// WEBPACK FOOTER //
// ./src/components/DashboardRecord/DashboardVisitInfo/index.jsx